import type { CmmdBoardTask, CmmdTaskWorkflow } from "@cmmd-forge/contracts";
import { milestoneIdFromGroupKey } from "./taskMilestoneGroups";
import {
  normalizeTaskStatus,
  type ProjectTaskBoardMove,
  type ProjectTaskPositionMove,
} from "./taskViewTypes";
import { canDropTaskInWorkflowColumn } from "./taskWorkflowGroups";

const CELL_DROP_PREFIX = "task-swimlane-cell:";

export interface TaskSwimlanePlacement {
  readonly laneId: string;
  readonly columnId: string;
}

export interface TaskSwimlaneMove extends TaskSwimlanePlacement {
  readonly afterCardId?: string | undefined;
}

export interface ResolvedTaskBoardDrag {
  readonly taskId: number;
  readonly move: ProjectTaskBoardMove;
}

export type ValidatedTaskBoardDrag =
  | { readonly kind: "allowed"; readonly drag: ResolvedTaskBoardDrag }
  | { readonly kind: "rejected" }
  | { readonly kind: "unresolved" };

interface TaskBoardDragOptions {
  readonly preservePosition?: boolean;
  readonly workflowCategoryByStageId?: ReadonlyMap<string, ProjectTaskPositionMove["toStatus"]>;
}

function isWorkflowCategoryMap(
  value: TaskBoardDragOptions | ReadonlyMap<string, ProjectTaskPositionMove["toStatus"]>,
): value is ReadonlyMap<string, ProjectTaskPositionMove["toStatus"]> {
  return "get" in value;
}

export function taskSwimlaneCellDropId(laneId: string, columnId: string): string {
  return `${CELL_DROP_PREFIX}${encodeURIComponent(laneId)}/${encodeURIComponent(columnId)}`;
}

function placementFromCellDropId(value: string): TaskSwimlanePlacement | null {
  if (!value.startsWith(CELL_DROP_PREFIX)) return null;
  const [laneId, columnId] = value.slice(CELL_DROP_PREFIX.length).split("/");
  if (laneId == null || columnId == null) return null;
  return { laneId: decodeURIComponent(laneId), columnId: decodeURIComponent(columnId) };
}

export function resolveTaskSwimlaneDrop(
  activeId: string,
  overId: string | null,
  placementByCardId: ReadonlyMap<string, TaskSwimlanePlacement>,
): TaskSwimlaneMove | null {
  if (overId === null || !placementByCardId.has(activeId) || overId === activeId) return null;
  const overPlacement = placementByCardId.get(overId);
  if (overPlacement != null) return { ...overPlacement, afterCardId: overId };
  return placementFromCellDropId(overId);
}

function positionMoveForDrop(
  drop: TaskSwimlaneMove,
  workflowCategoryByStageId: ReadonlyMap<string, ProjectTaskPositionMove["toStatus"]>,
): ProjectTaskPositionMove {
  return {
    toStatus: workflowCategoryByStageId.get(drop.columnId) ?? normalizeTaskStatus(drop.columnId),
    ...(workflowCategoryByStageId.has(drop.columnId) ? { toStageId: drop.columnId } : {}),
    ...(drop.afterCardId != null ? { afterTaskId: Number(drop.afterCardId) } : {}),
  };
}

function boardMoveForDrop(
  source: TaskSwimlanePlacement,
  drop: TaskSwimlaneMove,
  workflowCategoryByStageId: ReadonlyMap<string, ProjectTaskPositionMove["toStatus"]>,
): ProjectTaskBoardMove | null {
  const milestoneChanged = source.laneId !== drop.laneId;
  const positionChanged = source.columnId !== drop.columnId || drop.afterCardId != null;
  if (!milestoneChanged && !positionChanged) return null;
  if (!milestoneChanged) {
    return { kind: "position", move: positionMoveForDrop(drop, workflowCategoryByStageId) };
  }

  const milestoneId = milestoneIdFromGroupKey(drop.laneId);
  return positionChanged
    ? {
        kind: "lane-position",
        milestoneId,
        move: positionMoveForDrop(drop, workflowCategoryByStageId),
      }
    : { kind: "milestone", milestoneId };
}

export function resolveTaskBoardDrag(
  activeId: string,
  overId: string | null,
  placementByCardId: ReadonlyMap<string, TaskSwimlanePlacement>,
  optionsOrWorkflowMap:
    | TaskBoardDragOptions
    | ReadonlyMap<string, ProjectTaskPositionMove["toStatus"]> = {},
): ResolvedTaskBoardDrag | null {
  const options: TaskBoardDragOptions = isWorkflowCategoryMap(optionsOrWorkflowMap)
    ? { workflowCategoryByStageId: optionsOrWorkflowMap }
    : optionsOrWorkflowMap;
  const source = placementByCardId.get(activeId);
  const resolvedDrop = resolveTaskSwimlaneDrop(activeId, overId, placementByCardId);
  const preservePosition = options.preservePosition ?? true;
  const drop =
    resolvedDrop != null && !preservePosition
      ? { laneId: resolvedDrop.laneId, columnId: resolvedDrop.columnId }
      : resolvedDrop;
  if (source == null || drop == null) return null;

  const move = boardMoveForDrop(source, drop, options.workflowCategoryByStageId ?? new Map());
  return move == null ? null : { taskId: Number(activeId), move };
}

function destinationColumnId(move: ProjectTaskBoardMove): string | null {
  if (move.kind === "milestone") return null;
  return move.move.toStageId ?? move.move.toStatus;
}

export function validateTaskBoardDrag(
  drag: ResolvedTaskBoardDrag | null,
  tasks: readonly CmmdBoardTask[],
  workflow: CmmdTaskWorkflow | null | undefined,
): ValidatedTaskBoardDrag {
  if (drag == null) return { kind: "unresolved" };
  const task = tasks.find((candidate) => candidate.id === drag.taskId);
  const destinationId = destinationColumnId(drag.move);
  if (
    task != null &&
    destinationId != null &&
    !canDropTaskInWorkflowColumn(task, destinationId, workflow)
  ) {
    return { kind: "rejected" };
  }
  return { kind: "allowed", drag };
}
